import { useEffect, useRef, useState } from "react";
import { puedeGuardar } from "../../../NuevoPedido/componetizado/utils/lineas";
import {
  actualizarPedido,
  actualizarRenglon,
  crearRenglonPedido,
  darDeBajaRenglon,
} from "../services/edicionService";
import { esRenglonExistente, lineaARenglon } from "../utils/renglones";

// Estado con el que el original reprograma el pedido cuando se cambia la fecha.
const ESTADO_REPROGRAMADO = 245;

const PROGRESO_INICIAL = {
  operaciones: null, // lista armada en el primer intento; los reintentos usan la misma.
  realizadas: 0,
};

// Habilitación de "Guardar" y guardado de la edición.
//
// Se arma una lista de operaciones (PUT pedido → bajas → PUT existentes →
// POST nuevos) y se ejecutan en orden. Si una falla, al reintentar se sigue
// desde esa misma operación: no se repiten las ya hechas (en particular, los
// POST de renglones nuevos no se duplican).
export const useGuardarEdicion = ({
  dataPedido,
  clienteSeleccionado,
  direccionSeleccionada,
  datosPedido,
  fechaModificada,
  pedidoCambio,
  datosRenglon,
  eliminados,
  onGuardado,
  onError,
}) => {
  const [disabled, setDisabled] = useState(false);
  const [guardadoParcial, setGuardadoParcial] = useState(false);
  const progresoRef = useRef({ ...PROGRESO_INICIAL });
  // Candado síncrono contra doble clic.
  const enviandoRef = useRef(false);

  useEffect(() => {
    setDisabled(!puedeGuardar(clienteSeleccionado, direccionSeleccionada, datosRenglon));
  }, [clienteSeleccionado, direccionSeleccionada, datosPedido, datosRenglon]);

  const armarOperaciones = () => {
    const usuario = localStorage.getItem("user");

    // Igual que el original: si se cambió la fecha el pedido vuelve a 245 y se
    // desasigna de la rendición; si no, conserva su estado.
    const pedido = {
      id_direccion: direccionSeleccionada.id,
      fecha_para: datosPedido.fecha_para,
      observaciones: datosPedido.observaciones,
      usuario,
      pedido_cambio: pedidoCambio ? 1 : 0,
      ...(fechaModificada
        ? { estado: ESTADO_REPROGRAMADO, uuid_rendicion: null }
        : { estado: dataPedido.estado }),
    };

    return [
      { descripcion: "los datos del pedido", ejecutar: () => actualizarPedido(dataPedido.id, pedido) },
      ...eliminados.map((idRenglon) => ({
        descripcion: "la baja de un producto",
        ejecutar: () => darDeBajaRenglon(idRenglon, usuario),
      })),
      ...datosRenglon.filter(esRenglonExistente).map((linea) => {
        const payload = lineaARenglon(linea, usuario);
        return {
          descripcion: "la actualización de un producto",
          ejecutar: () => actualizarRenglon(linea.id, payload),
        };
      }),
      ...datosRenglon
        .filter((linea) => !esRenglonExistente(linea))
        .map((linea) => {
          const payload = { uuid_pedido: dataPedido.uuid_pedido, ...lineaARenglon(linea, usuario) };
          return {
            descripcion: "el alta de un producto",
            ejecutar: () => crearRenglonPedido(payload),
          };
        }),
    ];
  };

  const guardar = async () => {
    if (enviandoRef.current) return;
    enviandoRef.current = true;
    setDisabled(true);

    const progreso = progresoRef.current;
    if (!progreso.operaciones) {
      progreso.operaciones = armarOperaciones();
    }
    const { operaciones } = progreso;

    try {
      for (let i = progreso.realizadas; i < operaciones.length; i++) {
        await operaciones[i].ejecutar();
        progreso.realizadas = i + 1;
      }
      onGuardado();
    } catch (err) {
      console.error("Error guardando la edición del pedido:", err);
      const fallida = operaciones[progreso.realizadas];

      if (progreso.realizadas === 0) {
        // Nada quedó guardado: un reintento toma lo que haya en pantalla.
        progreso.operaciones = null;
        onError("No se pudo guardar el pedido. Intente nuevamente.");
      } else {
        setGuardadoParcial(true);
        onError(
          `Se guardó parte de los cambios, pero falló ${fallida.descripcion}. ` +
            `Presione "Guardar" para reintentar lo pendiente.`,
        );
      }
      // Sólo se libera ante un error; si se guardó bien queda bloqueado hasta cerrar.
      enviandoRef.current = false;
      setDisabled(false);
    }
  };

  return { disabled, guardar, guardadoParcial };
};
